import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  normalizeRelease,
  type DevOpsConnection,
  type DevOpsProject,
  type DevOpsRelease,
  type DevOpsReleaseApiRecord,
  type DevOpsReleaseCreatePayload,
  type DevOpsReleaseFormData,
  type DevOpsOverviewClientRef,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import { swallowedFailure } from "../shared/swallowedFind";
import { clientsForProject, type ProjectClientsView } from "../shared/projectClients";
import { exportToCsv } from "shared/common/utils/export";
import {
  RELEASE_EXPORT_COLUMNS,
  slugForFileName,
  toReleaseExportRow,
  type DevOpsExportScope,
  type ExportRecord,
} from "../devopsExports";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type ReleaseSortKey = "name" | "definitionName" | "status" | "createdOn";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<ReleaseSortKey> = new Set(["createdOn"]);

/**
 * Normalise a ServiceStore `find()` response into a plain array of rows.
 * Paginated services return `{ data, total }`; custom non-paginated finds may
 * return the bare array — tolerate both. (Replaces the removed `unwrapList`.)
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * View store for the Releases status board (deploy-side counterpart of the
 * Pipelines board).
 *
 * Plain class with `makeAutoObservable` (route ViewStores never extend
 * ViewStore). Backed by registered `@intrasync/stores` ServiceStores
 * (`devops-connections`, `devops-projects`, `devops-releases`). Lists
 * connections, then their live Azure DevOps projects + releases. The only write
 * is `createRelease` (start a release for a definition) — operator/admin only,
 * enforced by the API (`requireActionAccess`); the UI hides the button via
 * {@link canManage}. Connection/project selector shape is identical to
 * PipelinesBoardStore.
 *
 * `connections` is a computed view over the `devops-connections` entity cache;
 * `projects`/`releases` are scoped, re-queried Azure proxies read from each
 * `find()` RESPONSE (the entity cache accumulates across queries, so it is not
 * used for these volatile, per-selection lists).
 *
 * WHICH connection/project is the shared `devopsScopeStore`'s fact, not this
 * store's — see PipelinesBoardStore for the rule. Only the definition filter
 * is board-local.
 */
class ReleasesStore {
  rootStore: RootStore;

  projects: DevOpsProject[] = [];
  releases: DevOpsRelease[] = [];

  /** Optional release-definition filter (string for the select; "" = all). */
  selectedDefinitionId: string | null = null;

  connectionsLoading = false;
  projectsLoading = false;
  releasesLoading = false;
  error: string | null = null;

  // Header filter (applied client-side over the loaded release list).
  searchQuery = "";

  // Column sort (applied client-side over the filtered rows, like the search)
  sortKey: ReleaseSortKey = "createdOn";
  sortDir: SortDir = "desc";

  // Create Release dialog state
  formData: DevOpsReleaseFormData = {};
  saving = false;
  formError: string | null = null;

  /**
   * Sequence stamps for the two SCOPED reads — the same discipline as
   * `DevOpsSubscriptionsStore.projectsSeq` and `DevOpsProjectStore.loadSeq`.
   * Project A is chosen, then B. B answers first and paints; A's slower
   * answer must NOT repaint the grid while the shared scope says B, or the
   * page would offer B's project beside A's rows and an action would combine
   * the two. A superseded read writes nothing at all — not the rows, not its
   * error, and not the spinner its `finally` would otherwise clear out from
   * under the read that is still running.
   */
  private projectsSeq = 0;
  private releasesSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ----

  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  private get projectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }

  private get releasesService(): ServiceStoreType | null {
    return (this.rootStore.devopsReleasesStore as ServiceStoreType | null) ?? null;
  }

  /** Connection list — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- The shared scope ----

  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  get selectedConnectionId(): string | null {
    return this.scopeStore?.connectionId ?? null;
  }

  get selectedProjectId(): string | null {
    return this.scopeStore?.projectId ?? null;
  }

  /** Clients linked to the scoped project (from a loaded overview; never fetched here). */
  get projectClients(): ProjectClientsView {
    return clientsForProject(
      this.rootStore,
      this.selectedConnectionId,
      this.selectedProjectId
    );
  }

  get clientsForCurrentProject(): DevOpsOverviewClientRef[] {
    return this.projectClients.clients;
  }

  // ---- Derived ----

  /**
   * Whether the current user may start releases (the operator/admin tier).
   * The API is the real enforcement point; this only governs whether the
   * button renders. Tier resolution is centralised in `auth/tier.ts`.
   */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  get selectedConnection(): DevOpsConnection | null {
    if (!this.selectedConnectionId) return null;
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) || null
    );
  }

  get selectedProject(): DevOpsProject | null {
    if (!this.selectedProjectId) return null;
    return this.projects.find((p) => p.id === this.selectedProjectId) || null;
  }

  get totalCount(): number {
    return this.releases.length;
  }

  /** How many releases survive the header search — what the table renders. */
  get filteredCount(): number {
    return this.filteredReleases.length;
  }

  /**
   * The sentence the board prints under the table, or null when nothing is
   * loaded. THIS IS THE MOST IMPORTANT ONE OF THE THREE BOARDS.
   *
   * `devops-releases.find` calls Azure's release.getReleases(project,
   * definitionId) with no `top`, and Azure's Get Releases endpoint defaults to
   * the 50 MOST RECENT releases. So on any project with real history this board
   * is showing a page, not the history — and it looks exactly like a complete
   * list. That is the failure mode this note exists for: an operator scanning
   * for "when did we last release X" concludes "never" because X fell off page
   * one.
   *
   * The cap is Azure's, not ours, and it is not detectable from the response
   * (a project with exactly 50 releases and a project with 5,000 return the
   * same shape). The actionable escape is the Definition filter, which pushes
   * `definitionId` down to Azure so the page is drawn from one definition's
   * history instead of the project's — so the note says so.
   */
  get upstreamPagingNote(): string | null {
    if (this.totalCount === 0) return null;
    const scoped = this.selectedDefinitionId
      ? " for the selected definition"
      : "";
    return `Showing the ${this.totalCount} most recent release${
      this.totalCount === 1 ? "" : "s"
    } Azure DevOps returned${scoped}. Azure caps this read at its own page size (50 by default), so older releases are not listed — filter by definition to page through one pipeline's history.`;
  }

  // ---- CSV export ---------------------------------------------------------

  /** The connection/project this board was read through, for the export scope
   *  columns — a board CSV with no scope column is uninterpretable later. */
  get exportScope(): DevOpsExportScope {
    return {
      connectionName: this.selectedConnection?.name ?? "",
      projectName: this.selectedProject?.name ?? this.selectedProjectId ?? "",
    };
  }

  /**
   * The board as flat CSV records — exactly what is ON SCREEN
   * (`sortedReleases` = the name search, then the active column sort).
   *
   * Note this inherits the Azure page cap described in
   * {@link upstreamPagingNote}: the CSV is the page you were looking at, not
   * the project's release history. The caption on screen says so before the
   * download happens.
   */
  get exportRows(): ExportRecord[] {
    const scope = this.exportScope;
    return this.sortedReleases.map((r) => toReleaseExportRow(r, scope));
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /** Download the on-screen releases as CSV. False (never a throw) on an empty
   *  set; the header also disables the button on {@link canExport}. */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      const project = this.exportScope.projectName || "releases";
      exportToCsv(rows, RELEASE_EXPORT_COLUMNS, {
        fileName: `releases-${slugForFileName(project)}-${new Date()
          .toISOString()
          .slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Releases CSV export failed:", err);
      return false;
    }
  }

  get activeCount(): number {
    return this.releases.filter((r) => r.status === "active").length;
  }

  get draftCount(): number {
    return this.releases.filter((r) => r.status === "draft").length;
  }

  get abandonedCount(): number {
    return this.releases.filter((r) => r.status === "abandoned").length;
  }

  /**
   * Distinct release definitions present in the loaded releases — powers both
   * the header's definition filter and the Create Release dialog's picker
   * (`devops-releases` exposes no separate definition listing).
   */
  get definitionOptions(): Array<{ id: string; name: string }> {
    const byId = new Map<string, string>();
    for (const release of this.releases) {
      if (release.definitionId && !byId.has(release.definitionId)) {
        byId.set(
          release.definitionId,
          release.definitionName || `Definition ${release.definitionId}`
        );
      }
    }
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }

  get filteredReleases(): DevOpsRelease[] {
    if (!this.searchQuery.trim()) return this.releases;
    const q = this.searchQuery.toLowerCase().trim();
    return this.releases.filter((r) => r.name?.toLowerCase().includes(q));
  }

  /**
   * The rows the table renders: name search, then the active column sort
   * (created newest-first by default). Nulls sort last in either direction.
   */
  get sortedReleases(): DevOpsRelease[] {
    const key = this.sortKey;
    const valueOf = (r: DevOpsRelease): SortValue => {
      switch (key) {
        case "name":
          return textValue(r.name);
        case "definitionName":
          // The grid falls back to "Definition <id>" — sort on the same value.
          return textValue(r.definitionName ?? r.definitionId);
        case "status":
          return textValue(r.status);
        case "createdOn":
          return dateValue(r.createdOn);
      }
    };
    return sortRows(this.filteredReleases, valueOf, this.sortDir);
  }

  // ---- Filter / sort setters ----

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (created newest-first, text A→Z). */
  setSort(key: ReleaseSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ----

  /**
   * Entry point from the route (and from a Project 360 tab). A scope chosen
   * anywhere else is honoured as-is; only an EMPTY scope auto-picks.
   */
  async initialize() {
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // A sign-out during the read leaves `error` null (it is no failure of the
    // next person's), so the `error` check below cannot end the sequence.
    if (ended()) return;
    // A remembered connection that no longer exists is dropped (with its
    // project) so the auto-pick below runs — never after a FAILED load, which
    // `loadConnections` records on `error`. See PipelinesBoardStore.initialize.
    // A failed connections load ends the sequence. There is no list to
    // reconcile against and no connection to read a project for, and going on
    // would replace the CAUSAL error with whatever the doomed project read
    // reports — telling the operator the projects are unavailable when the
    // truth is that HQ could not reach the connections at all.
    if (this.error) return;
    this.scopeStore?.reconcileConnections(this.connections.map((c) => c.id));
    if (this.selectedConnectionId) {
      await this.adoptScope();
    } else if (this.connections.length > 0) {
      await this.selectConnection(this.connections[0].id);
    }
  }

  /**
   * Load this board for a scope set elsewhere. Same rule as
   * PipelinesBoardStore.adoptScope: a project the connection no longer lists
   * is cleared (project only), nothing is guessed in its place, and a failed
   * project read leaves the scope untouched.
   */
  private async adoptScope() {
    const ended = sessionMark(this.rootStore);
    await this.loadProjects();
    // As in initialize: a sign-out leaves no error, and going on would clear
    // the shared scope's project against the list reset() emptied.
    if (ended() || this.error) return;
    const projectId = this.selectedProjectId;
    if (!projectId) {
      runInAction(() => {
        this.selectedDefinitionId = null;
        this.releases = [];
      });
      return;
    }
    const project = this.projects.find((p) => p.id === projectId);
    if (!project) {
      runInAction(() => {
        this.scopeStore?.setProject(null);
        this.selectedDefinitionId = null;
        this.releases = [];
      });
      return;
    }
    if (!this.scopeStore?.projectName) {
      this.scopeStore?.setScope({ projectName: project.name });
    }
    await this.loadReleases();
  }

  /**
   * Route onExit. There is no live feed to tear down on this board (yet — the
   * plan's finding), so the only thing worth discarding is a half-typed
   * Create Release form: it must not reappear pre-filled on the next visit as
   * if someone had meant it.
   */
  dispose() {
    this.formData = {};
    this.formError = null;
  }

  async loadConnections() {
    this.connectionsLoading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      const result = await this.connectionsService?.find({
        query: { $limit: 200, $sort: { name: 1 } },
      });
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never an org with no connections. It matters twice over here: the
      // cache is empty on a fresh session (`enableStorage: false`), so a
      // silent failure would hand `initialize`'s reconcile an empty list and
      // wipe the remembered scope — the one thing reconcileConnections is
      // documented never to do on a failed load.
      if (result == null) {
        throw swallowedFailure(this.connectionsService, "errorOnFind", "Failed to load connections");
      }
    } catch (err: unknown) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load connections";
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope (a new connection clears the project). */
  async selectConnection(connectionId: string | null) {
    runInAction(() => {
      // Invalidate both reads in flight for the scope being left. The loaders
      // below stamp their own sequence when a selection IS made, but clearing
      // one loads nothing — without this bump the previous scope's answer
      // still matches and paints itself onto a board that has moved on.
      this.projectsSeq += 1;
      this.releasesSeq += 1;
      this.projectsLoading = false;
      this.releasesLoading = false;
      this.scopeStore?.setScope({ connectionId, projectId: null, projectName: null });
      this.selectedDefinitionId = null;
      this.projects = [];
      this.releases = [];
    });
    if (connectionId) {
      await this.loadProjects();
      // Auto-select the connection's default project (or the first one).
      const conn = this.selectedConnection;
      const defaultProj =
        (conn?.defaultProject &&
          this.projects.find(
            (p) =>
              p.name === conn.defaultProject || p.id === conn.defaultProject
          )) ||
        this.projects[0];
      if (defaultProj) {
        await this.selectProject(defaultProj.id);
      }
    }
  }

  async loadProjects() {
    if (!this.selectedConnectionId) return;
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    try {
      // Live read-only proxy to Azure DevOps via the connection. Scoped +
      // re-queried per selection → read the find() response, not the cache.
      // No `$limit`: `devops-projects.find` reads only `connectionId` and
      // calls Azure's core.getProjects(), returning whatever page Azure gives
      // back. A `$limit` here was inert — it looked like an enforced cap and
      // was not one. Azure's own paging is the real bound; if an org ever has
      // more projects than one page, the picker needs a search, not a number.
      const result = await this.projectsService?.find({
        query: { connectionId: this.selectedConnectionId },
      });
      // A null result is a swallowed failure, not an empty org (see
      // shared/swallowedFind.ts) — adoptScope must not clear the project on it.
      if (seq !== this.projectsSeq) return; // a newer scope superseded this
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(
          result as unknown as { data?: DevOpsProject[] } | DevOpsProject[],
        );
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a scope
      // this board has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load projects";
        }
      });

      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope, name included. */
  async selectProject(projectId: string | null) {
    runInAction(() => {
      // Invalidate the board read in flight for the project being left.
      this.releasesSeq += 1;
      this.releasesLoading = false;
      this.scopeStore?.setProject(
        projectId,
        this.projects.find((p) => p.id === projectId)?.name ?? null
      );
      this.selectedDefinitionId = null;
      this.releases = [];
    });
    if (projectId) {
      await this.loadReleases();
    }
  }

  async selectDefinition(definitionId: string | null) {
    runInAction(() => {
      this.selectedDefinitionId = definitionId;
      this.releases = [];
    });
    await this.loadReleases();
  }

  async loadReleases() {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    const seq = ++this.releasesSeq;
    this.releasesLoading = true;
    this.error = null;
    try {
      // Live read-only proxy. The API reads `project` (not projectId) and an
      // optional `definitionId`, and returns a bare array of records with a
      // NUMERIC Azure ReleaseStatus enum; normalise each into the flat shape.
      //
      // NO `$limit`: `devops-releases.find` reads connectionId/project/
      // definitionId and ignores the rest, so the old `$limit: 200` was inert.
      // Azure's own default page size (50) is the real cap, and it is the one
      // the board discloses — see {@link upstreamPagingNote}.
      const query: Record<string, unknown> = {
        connectionId: this.selectedConnectionId,
        project: this.selectedProjectId,
      };
      if (this.selectedDefinitionId) {
        query.definitionId = Number(this.selectedDefinitionId);
      }
      const result = await this.releasesService?.find({ query });
      if (seq !== this.releasesSeq) return; // a newer project superseded this
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never a project with no releases — and must not empty a populated
      // grid with nothing saying the read failed.
      if (result == null) {
        throw swallowedFailure(this.releasesService, "errorOnFind", "Failed to load releases");
      }
      runInAction(() => {
        this.releases = rowsOf<DevOpsReleaseApiRecord>(
          result as unknown as
            | { data?: DevOpsReleaseApiRecord[] }
            | DevOpsReleaseApiRecord[],
        ).map(normalizeRelease);
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a project
      // this board has left.
      if (seq !== this.releasesSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load releases";
        }
      });

      console.error("Failed to load DevOps releases:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.releasesSeq) this.releasesLoading = false;
      });
    }
  }

  /** Header refresh — re-pull releases for the current selection. */
  async reload() {
    if (this.selectedConnectionId && this.selectedProjectId) {
      await this.loadReleases();
    } else {
      await this.initialize();
    }
  }

  // ---- Create Release (operator/admin) --------------------------------------

  startCreate() {
    this.formError = null;
    this.formData = {
      // Pre-fill from the active definition filter when one is selected.
      definitionId: this.selectedDefinitionId || "",
      description: "",
    };
  }

  setFormField(field: keyof DevOpsReleaseFormData, value: string) {
    this.formData = { ...this.formData, [field]: value } as DevOpsReleaseFormData;
  }

  get isFormValid(): boolean {
    if (!this.selectedConnectionId || !this.selectedProjectId) return false;
    const id = Number(this.formData.definitionId);
    return !!this.formData.definitionId?.trim() && Number.isFinite(id) && id > 0;
  }

  /**
   * Start a release via the `devops-releases` createRelease custom method
   * (POST/create). The API reads `data.project` (not projectId) and requires a
   * NUMERIC definitionId. Returns false (and sets `formError`) on failure so
   * the dialog stays open and surfaces the API error.
   */
  async createRelease(): Promise<boolean> {
    if (!this.isFormValid || !this.selectedConnectionId || !this.selectedProjectId) {
      this.formError = "A numeric release definition id is required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    const ended = sessionMark(this.rootStore);
    try {
      const payload: DevOpsReleaseCreatePayload = {
        connectionId: this.selectedConnectionId,
        project: this.selectedProjectId,
        definitionId: Number(this.formData.definitionId),
      };
      if (this.formData.description?.trim()) {
        payload.description = this.formData.description.trim();
      }
      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refusal (a bad
      // PAT, an unknown definition) as null, which read as a started release
      // and closed the dialog with the reason lost.
      await this.releasesService?.create(payload as never, THROW_ON_ERROR);

      await this.loadReleases();
      // Started; the reload swallows a sign-out, and the form is reset already.
      if (ended()) return true;
      runInAction(() => {
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Failed to create release";
        }
      });

      console.error("createRelease failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.projects = [];
    this.releases = [];
    this.selectedDefinitionId = null;
    this.connectionsLoading = false;
    this.projectsLoading = false;
    this.releasesLoading = false;
    this.error = null;
    this.searchQuery = "";
    this.sortKey = "createdOn";
    this.sortDir = "desc";
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.projectsSeq += 1;
    this.releasesSeq += 1;
  }
}

export default ReleasesStore;
